import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  AlertTitle,
  Tooltip,
  Divider,
  Grid
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';

const DublinTrunkManager = ({ onCreateTrunkClick, phoneNumbers, showSnackbar }) => {
  const [dublinTrunks, setDublinTrunks] = useState([]);
  const [loadingDublinTrunks, setLoadingDublinTrunks] = useState(false);
  const [selectedTrunk, setSelectedTrunk] = useState(null);
  const [trunkDetailsDialog, setTrunkDetailsDialog] = useState(false);
  const [phoneToConnectDialog, setPhoneToConnectDialog] = useState(false);
  const [selectedPhoneToConnect, setSelectedPhoneToConnect] = useState(null);
  const [trunkToConnect, setTrunkToConnect] = useState(null);
  const [connectingPhone, setConnectingPhone] = useState(false);
  const [sipUriDialog, setSipUriDialog] = useState(false);
  const [trunkToEditSip, setTrunkToEditSip] = useState(null);
  const [newSipUri, setNewSipUri] = useState('');
  const [newSipFriendlyName, setNewSipFriendlyName] = useState('');
  const [updatingSipUri, setUpdatingSipUri] = useState(false);
  
  const { user, token: auth } = useAuth();
  const isAdmin = user?.role === 'admin';
  
  useEffect(() => {
    fetchDublinTrunks();
  }, []);
  
  const fetchDublinTrunks = async () => {
    try {
      setLoadingDublinTrunks(true);
      const response = await fetch(`${API_URL}/api/twilio/dublin-trunks`, {
        headers: {
          'Authorization': `Bearer ${auth}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          console.log('Dublin trunks:', data.trunks);
          // Fetch details for each trunk to get origination URLs
          const trunksWithDetails = await Promise.all(
            data.trunks.map(async (trunk) => {
              const detailsResponse = await fetch(`${API_URL}/api/twilio/dublin-trunks/${trunk.sid}`, {
                headers: {
                  'Authorization': `Bearer ${auth}`
                }
              });
              
              if (detailsResponse.ok) {
                const detailsData = await detailsResponse.json();
                if (detailsData.success) {
                  return detailsData.trunk;
                }
              }
              return trunk;
            })
          );
          setDublinTrunks(trunksWithDetails);
        } else {
          console.error('Error fetching Dublin trunks:', data.error);
          showSnackbar(`Error: ${data.error}`, 'error');
        }
      } else {
        const errorData = await response.json();
        console.error('Error response:', errorData);
        showSnackbar(`Error: ${errorData.error}`, 'error');
      }
    } catch (error) {
      console.error('Error fetching Dublin trunks:', error);
      showSnackbar('Error fetching Dublin trunks', 'error');
    } finally {
      setLoadingDublinTrunks(false);
    }
  };
  
  const fetchDublinTrunkDetails = async (trunkSid) => {
    try {
      const response = await fetch(`${API_URL}/api/twilio/dublin-trunks/${trunkSid}`, {
        headers: {
          'Authorization': `Bearer ${auth}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          return data.trunk;
        } else {
          console.error('Error fetching Dublin trunk details:', data.error);
          showSnackbar(`Error: ${data.error}`, 'error');
        }
      } else {
        const errorData = await response.json();
        console.error('Error response:', errorData);
        showSnackbar(`Error: ${errorData.error}`, 'error');
      }
    } catch (error) {
      console.error('Error fetching Dublin trunk details:', error);
      showSnackbar('Error fetching Dublin trunk details', 'error');
    }
    return null;
  };
  
  const handleDeleteDublinTrunk = async (trunkSid) => {
    try {
      const response = await fetch(`${API_URL}/api/twilio/dublin-trunks/${trunkSid}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${auth}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Remove the trunk from the list
          setDublinTrunks(prevTrunks => prevTrunks.filter(trunk => trunk.sid !== trunkSid));
          showSnackbar('Dublin trunk deleted successfully', 'success');
        } else {
          showSnackbar(`Error: ${data.error}`, 'error');
        }
      } else {
        const errorData = await response.json();
        showSnackbar(`Error: ${errorData.error}`, 'error');
      }
    } catch (error) {
      console.error('Error deleting Dublin trunk:', error);
      showSnackbar('Error deleting Dublin trunk', 'error');
    }
  };
  
  const handleConnectPhoneToTrunk = async (phoneId, trunkSid) => {
    try {
      const response = await fetch(`${API_URL}/api/twilio/numbers/${phoneId}/connect-dublin-trunk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth}`
        },
        body: JSON.stringify({
          trunkSid
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          showSnackbar('Phone number connected to Dublin trunk successfully', 'success');
          // Refresh Dublin trunks
          fetchDublinTrunks();
        } else {
          showSnackbar(`Error: ${data.error}`, 'error');
        }
      } else {
        const errorData = await response.json();
        showSnackbar(`Error: ${errorData.error}`, 'error');
      }
    } catch (error) {
      console.error('Error connecting phone to Dublin trunk:', error);
      showSnackbar('Error connecting phone to Dublin trunk', 'error');
    }
  };
  
  const handleOpenTrunkDetailsDialog = (trunk) => {
    setSelectedTrunk(trunk);
    setTrunkDetailsDialog(true);
  };

  const handleCloseTrunkDetailsDialog = () => {
    setSelectedTrunk(null);
    setTrunkDetailsDialog(false);
  };
  
  const handleOpenConnectPhoneDialog = (trunk) => {
    setTrunkToConnect(trunk);
    setPhoneToConnectDialog(true);
  };
  
  const handleCloseConnectPhoneDialog = () => {
    setTrunkToConnect(null);
    setSelectedPhoneToConnect(null);
    setPhoneToConnectDialog(false);
  };
  
  const handleConnectPhoneToTrunkSubmit = async () => {
    if (!selectedPhoneToConnect || !trunkToConnect) {
      showSnackbar('Please select a phone number', 'error');
      return;
    }
    
    setConnectingPhone(true);
    try {
      await handleConnectPhoneToTrunk(selectedPhoneToConnect.id, trunkToConnect.sid);
      handleCloseConnectPhoneDialog();
    } catch (error) {
      console.error('Error connecting phone to trunk:', error);
    } finally {
      setConnectingPhone(false);
    }
  };
  
  const handleOpenSipUriDialog = (trunk) => {
    setTrunkToEditSip(trunk);
    // Get existing SIP URI if available
    if (trunk.origination_urls && trunk.origination_urls.length > 0) {
      setNewSipUri(trunk.origination_urls[0].sip_url);
      setNewSipFriendlyName(trunk.origination_urls[0].friendly_name || '');
    } else {
      setNewSipUri('');
      setNewSipFriendlyName('');
    }
    setSipUriDialog(true);
  };
  
  const handleCloseSipUriDialog = () => {
    setTrunkToEditSip(null);
    setNewSipUri('');
    setNewSipFriendlyName('');
    setSipUriDialog(false);
  };
  
  const handleUpdateSipUri = async () => {
    if (!trunkToEditSip || !newSipUri) {
      showSnackbar('Please enter a valid SIP URI', 'error');
      return;
    }
    
    setUpdatingSipUri(true);
    try {
      const response = await fetch(`${API_URL}/api/twilio/dublin-trunks/${trunkToEditSip.sid}/sip-uri`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth}`
        },
        body: JSON.stringify({
          sip_uri: newSipUri,
          friendly_name: newSipFriendlyName || `SIP URI for ${trunkToEditSip.sid}`
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          showSnackbar('SIP URI updated successfully', 'success');
          // Update the trunk in the local state
          setDublinTrunks(prevTrunks => 
            prevTrunks.map(trunk => 
              trunk.sid === trunkToEditSip.sid 
                ? { 
                    ...trunk, 
                    origination_urls: [
                      {
                        sip_url: newSipUri,
                        friendly_name: newSipFriendlyName,
                        sid: data.origination_url_sid
                      }
                    ]
                  }
                : trunk
            )
          );
          handleCloseSipUriDialog();
        } else {
          showSnackbar(`Error: ${data.error}`, 'error');
        }
      } else {
        const errorData = await response.json();
        showSnackbar(`Error: ${errorData.error}`, 'error');
      }
    } catch (error) {
      console.error('Error updating SIP URI:', error);
      showSnackbar('Error updating SIP URI', 'error');
    } finally {
      setUpdatingSipUri(false);
    }
  };
  
  // Function to get phone number by trunk SID
  const getPhoneForTrunk = (trunkSid) => {
    return phoneNumbers.find(phone => phone.twilio_trunk_sid === trunkSid);
  };
  
  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Dublin SIP Trunks
        </Typography>
        <Box>
          <Button
            variant="outlined"
            color="primary"
            onClick={fetchDublinTrunks}
            sx={{ mr: 1 }}
            disabled={loadingDublinTrunks}
          >
            {loadingDublinTrunks ? <CircularProgress size={24} /> : 'Refresh'}
          </Button>
          {isAdmin && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={onCreateTrunkClick}
              disabled={loadingDublinTrunks}
            >
              Create New Trunk
            </Button>
          )}
        </Box>
      </Box>
      
      {loadingDublinTrunks ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : dublinTrunks.length === 0 ? (
        <Typography variant="body1" sx={{ textAlign: 'center', py: 5 }}>
          No Dublin SIP trunks found. Create a Dublin trunk to get started.
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Trunk Name</TableCell>
                <TableCell>Trunk SID</TableCell>
                <TableCell>Phone Number</TableCell>
                <TableCell>SIP URI</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dublinTrunks.map((trunk) => {
                const phone = getPhoneForTrunk(trunk.sid);
                // Get SIP URIs from origination URLs
                const sipUris = trunk.origination_urls?.map(url => url.sip_url) || [];
                
                return (
                  <TableRow key={trunk.sid}>
                    <TableCell>{trunk.friendly_name}</TableCell>
                    <TableCell>
                      <Tooltip title={trunk.sid}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                          {trunk.sid}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {phone ? (
                        <Chip
                          label={phone.phone_number}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ) : (
                        <Chip
                          label="No phone assigned"
                          size="small"
                          variant="outlined"
                          color="default"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {sipUris.length > 0 ? (
                        <Box>
                          {sipUris.map((uri, index) => (
                            <Chip
                              key={index}
                              label={uri}
                              size="small"
                              color="success"
                              variant="outlined"
                              sx={{ mb: 0.5 }}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Chip
                          label="No SIP URIs"
                          size="small"
                          variant="outlined"
                          color="default"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {new Date(trunk.date_created).toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      {isAdmin && (
                        <>
                          {!phone && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              sx={{ mr: 1 }}
                              onClick={() => handleOpenConnectPhoneDialog(trunk)}
                            >
                              Connect Phone
                            </Button>
                          )}
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            sx={{ mr: 1 }}
                            onClick={() => handleOpenTrunkDetailsDialog(trunk)}
                          >
                            View Details
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="info"
                            sx={{ mr: 1 }}
                            onClick={() => handleOpenSipUriDialog(trunk)}
                          >
                            Edit SIP URI
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => handleDeleteDublinTrunk(trunk.sid)}
                          >
                            Delete
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Trunk Details Dialog */}
      <Dialog
        open={trunkDetailsDialog}
        onClose={handleCloseTrunkDetailsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Dublin SIP Trunk Details
          <IconButton
            aria-label="close"
            onClick={handleCloseTrunkDetailsDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedTrunk && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Trunk Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Friendly Name</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>{selectedTrunk.friendly_name || 'None'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">SID</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>{selectedTrunk.sid}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Date Created</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {new Date(selectedTrunk.date_created).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Recording</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedTrunk.recording ? selectedTrunk.recording.mode : 'Disabled'}
                  </Typography>
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" gutterBottom>
                Origination URLs
              </Typography>
              {selectedTrunk.origination_urls && selectedTrunk.origination_urls.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>SIP URL</TableCell>
                        <TableCell>Priority</TableCell>
                        <TableCell>Weight</TableCell>
                        <TableCell>Enabled</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedTrunk.origination_urls.map((url, index) => (
                        <TableRow key={index}>
                          <TableCell>{url.sip_url}</TableCell>
                          <TableCell>{url.priority}</TableCell>
                          <TableCell>{url.weight}</TableCell>
                          <TableCell>{url.enabled ? 'Yes' : 'No'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No origination URLs configured
                </Typography>
              )}
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" gutterBottom>
                Connected Phone Numbers
              </Typography>
              {selectedTrunk.phone_numbers && selectedTrunk.phone_numbers.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Phone Number</TableCell>
                        <TableCell>SID</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedTrunk.phone_numbers.map((phone, index) => (
                        <TableRow key={index}>
                          <TableCell>{phone.phone_number}</TableCell>
                          <TableCell>{phone.sid}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No phone numbers connected to this trunk
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTrunkDetailsDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Connect Phone to Trunk Dialog */}
      <Dialog
        open={phoneToConnectDialog}
        onClose={handleCloseConnectPhoneDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Connect Phone Number to Dublin Trunk
          <IconButton
            aria-label="close"
            onClick={handleCloseConnectPhoneDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {trunkToConnect && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Select a phone number to connect to trunk: {trunkToConnect.friendly_name || trunkToConnect.sid}
              </Typography>
              
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel id="phone-select-label">Phone Number</InputLabel>
                <Select
                  labelId="phone-select-label"
                  value={selectedPhoneToConnect ? selectedPhoneToConnect.id : ''}
                  onChange={(e) => {
                    const phoneId = e.target.value;
                    const phone = phoneNumbers.find(p => p.id === phoneId);
                    setSelectedPhoneToConnect(phone);
                  }}
                  label="Phone Number"
                  disabled={connectingPhone}
                >
                  <MenuItem value="">
                    <em>Select a phone number</em>
                  </MenuItem>
                  {phoneNumbers
                    .filter(phone => !phone.twilio_trunk_sid)
                    .map((phone) => (
                      <MenuItem key={phone.id} value={phone.id}>
                        {phone.phone_number} ({phone.friendly_name || 'No name'})
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              
              {phoneNumbers.filter(phone => !phone.twilio_trunk_sid).length === 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  All phone numbers are already connected to trunks. Purchase a new number first.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseConnectPhoneDialog}
            disabled={connectingPhone}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConnectPhoneToTrunkSubmit}
            variant="contained" 
            color="primary"
            disabled={!selectedPhoneToConnect || connectingPhone}
          >
            {connectingPhone ? <CircularProgress size={24} /> : 'Connect'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* SIP URI Dialog */}
      <Dialog
        open={sipUriDialog}
        onClose={handleCloseSipUriDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit SIP URI
          <IconButton
            aria-label="close"
            onClick={handleCloseSipUriDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {trunkToEditSip && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Update SIP URI for trunk: {trunkToEditSip.friendly_name || trunkToEditSip.sid}
              </Typography>
              
              <TextField
                fullWidth
                label="SIP URI"
                value={newSipUri}
                onChange={(e) => setNewSipUri(e.target.value)}
                margin="normal"
                placeholder="sip:example.com:5060"
                helperText="SIP URI format: sip:domain:port?parameter=value"
                disabled={updatingSipUri}
              />
              
              <TextField
                fullWidth
                label="Friendly Name (Optional)"
                value={newSipFriendlyName}
                onChange={(e) => setNewSipFriendlyName(e.target.value)}
                margin="normal"
                placeholder="My SIP URI"
                disabled={updatingSipUri}
              />
              
              <Alert severity="info" sx={{ mt: 2 }}>
                <AlertTitle>For Dublin Trunks</AlertTitle>
                To use the Dublin edge, make sure to include <strong>transport=tls</strong> and <strong>edge=dublin</strong>
                parameters in your SIP URI.
                <br />
                Example: <code>sip:example.com:5061?transport=tls&edge=dublin</code>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseSipUriDialog}
            disabled={updatingSipUri}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateSipUri}
            variant="contained" 
            color="primary"
            disabled={!newSipUri || updatingSipUri}
          >
            {updatingSipUri ? <CircularProgress size={24} /> : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DublinTrunkManager; 